import React from "react";
import { observable, action } from "mobx";
import * as Api from "api";
import * as Agency from "api/admin/Agency";
import * as Developer from "api/admin/Developer";
import * as User from "api/admin/User";

interface Value {
  id?: number;
  loginId?: string;
  password?: string;
  isActive?: boolean;
  agencyId?: number;
  developerId?: number;
}

export class FormStore {
  public errors = new Api.Error<User.CreateParams>();

  @observable.ref
  public open = false;

  @observable.ref
  public agencies: Agency.IndexResponse[] = [];

  @observable.ref
  public developers: Developer.IndexResponse[] = [];

  @observable
  public value: Value = {};

  @action.bound
  public modal(open: boolean, value?: Value) {
    this.open = open;
    this.errors.clear();
    this.value = value || {
      isActive: true,
    };

    if (open) {
      this.fetchAgencies();
      this.fetchDevelopers();
    }
  }

  @action.bound
  private async fetchAgencies() {
    const response = await Agency.Api.index();
    this.agencies = response?.status === Api.Status.OK ? response.data : [];
  }

  @action.bound
  private async fetchDevelopers() {
    const response = await Developer.Api.index();
    this.developers = response?.status === Api.Status.OK ? response.data : [];
  }

  @action.bound
  public async save() {
    this.errors.clear();

    const response = this.value.id
      ? await User.Api.update(this.value.id, this.value)
      : await User.Api.create(this.value);

    if (response?.status === Api.Status.OK) {
      this.modal(false);
      return true;
    }

    this.errors.set(response?.data);

    return false;
  }
}

const Context = React.createContext<FormStore | null>(null);

export const FormStoreProvider: React.FC<{ value?: FormStore }> = ({
  children,
  value,
}) => {
  const store = React.useMemo(() => (value ? value : new FormStore()), [value]);
  return <Context.Provider value={store}>{children}</Context.Provider>;
};

export const useFormStore = () => {
  const formStore = React.useContext(Context);
  if (!formStore) {
    throw new Error("useFormStore must be used within a FormStoreProvider.");
  }
  return formStore;
};
